<div class="table-responsive settings-table-shell sp-um-table-shell card shadow-sm rounded-4 overflow-x-auto">
    <div v-if="is_loading" class="text-sm d-flex flex-column align-items-center justify-content-center text-center py-5 px-3 text-dark">
        <div class="spinner-border sp-spinner text-warning" role="status" aria-hidden="true"></div>
        <p class="text-secondary mt-3 mb-0">Loading users...</p>
    </div>

    <div v-else-if="!users.length" class="text-sm d-flex flex-column align-items-center justify-content-center text-center py-5 px-3 text-dark">
        <span class="sp-state-icon d-inline-flex align-items-center justify-content-center rounded-circle fs-5 mb-3 text-warning-emphasis bg-warning-subtle"><i class="bi bi-people"></i></span>
        <p class="fw-semibold mb-1">No users yet</p>
        <p class="text-secondary mb-0">Users you add will appear here.</p>
    </div>

    <table
        v-else
        class="table table-hover align-middle mb-0 sp-um-table global_data_table"
        data-search-placeholder="Search users"
        data-non-orderable-targets="[6]"
        data-page-length="5"
        data-order-column="0"
        data-order-direction="asc"
    >
        <thead>
            <tr>
                <th scope="col" class="text-uppercase text-secondary fw-bold align-bottom">User</th>
                <th scope="col" class="text-uppercase text-secondary fw-bold align-bottom">Role</th>
                <th scope="col" class="text-uppercase text-secondary fw-bold align-bottom">Sites</th>
                <th scope="col" class="text-uppercase text-secondary fw-bold align-bottom">Session</th>
                <th scope="col" class="text-uppercase text-secondary fw-bold align-bottom">Status</th>
                <th scope="col" class="text-uppercase text-secondary fw-bold align-bottom">Last login</th>
                <th scope="col" class="text-uppercase text-secondary fw-bold text-end align-bottom">Actions</th>
            </tr>
        </thead>

        <tbody>
            <tr v-for="user in users" :key="user.id" :class="{ 'is-inactive': !user.is_active }">
                <td data-label="Member" class="settings-user-member-cell">
                    <div class="sp-user-cell d-flex align-items-center gap-2">
                        <span class="sp-avatar-sm d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-circle user-select-none fw-bold text-dark bg-warning">{{ get_initials(user) }}</span>
                        <div class="sp-user-cell-text d-flex flex-column min-w-0">
                            <span class="sp-user-name d-inline-flex align-items-center fw-semibold gap-2 text-dark">
                                {{ get_full_name(user) }}
                                <span v-if="is_current_user(user)" class="sp-you-tag rounded-pill text-uppercase fw-bold py-1 px-2 text-warning-emphasis bg-warning-subtle">You</span>
                            </span>
                            <span class="sp-user-email d-block fw-normal text-secondary">{{ user.email || "-" }}</span>
                        </div>
                    </div>
                </td>
                <td data-label="Role">
                    <span class="badge rounded-pill bg-body-tertiary text-dark border fw-semibold d-inline-flex align-items-center text-nowrap text-capitalize gap-1 py-1 px-2 lh-sm">{{ user.role || "-" }}</span>
                </td>
                <td data-label="Sites" class="settings-user-properties-cell" :data-order="get_property_summary(user)">
                    <span v-if="!user.assigned_properties?.length" class="text-secondary">No sites assigned</span>
                    <div v-else class="sp-site-list d-flex flex-wrap gap-1" :title="get_all_property_names(user)">
                        <span v-for="property in user.assigned_properties.slice(0, 2)" :key="property.id || property.name" class="sp-site-chip d-inline-flex align-items-center text-truncate fw-medium gap-1 py-1 px-2 rounded-2 bg-body-tertiary border">
                            <i class="bi bi-geo-alt"></i>{{ property.name }}
                        </span>
                        <span v-if="user.assigned_properties.length > 2" class="sp-site-chip text-secondary sp-site-chip-more d-inline-flex align-items-center text-truncate fw-medium gap-1 py-1 px-2 rounded-2 bg-body-tertiary border">
                            +{{ user.assigned_properties.length - 2 }}
                        </span>
                    </div>
                </td>
                <td data-label="Session" class="text-secondary" :data-order="get_session_label(user)">
                    <span class="badge rounded-pill bg-body-tertiary text-dark border fw-semibold d-inline-flex align-items-center text-nowrap gap-1 py-1 px-2 lh-sm">
                        <i class="bi bi-clock-history"></i>{{ get_session_label(user) }}
                    </span>
                </td>
                <td data-label="Status">
                    <span class="sp-status d-inline-flex align-items-center rounded-pill text-nowrap gap-2 py-1 px-2 fw-semibold" :class="user.is_active ? 'is-active' : 'is-inactive'">
                        {{ user.is_active ? "Active" : "Inactive" }}
                    </span>
                </td>
                <td data-label="Last Login" class="text-secondary" :data-order="user.last_login || ''">
                    {{ get_last_login_label(user) }}
                </td>
                <td data-label="Actions" class="settings-user-actions-cell">
                    <div class="sp-row-actions d-flex justify-content-end gap-1">
                        <button
                            type="button"
                            class="btn d-inline-flex align-items-center justify-content-center gap-2 text-sm btn-light border btn-sm text-nowrap"
                            @click="open_edit_user(user)"
                            :aria-label="`Edit ${get_full_name(user)}`"
                        >
                            <i class="bi bi-pencil"></i>
                            <span>Edit</span>
                        </button>

                        <button
                            type="button"
                            :class="['btn d-inline-flex align-items-center justify-content-center gap-2 text-sm btn-sm text-nowrap', user.is_active ? 'text-danger bg-danger-subtle border-0' : 'text-success bg-success-subtle border-0']"
                            @click="toggle_user_status(user)"
                            :disabled="is_busy || (is_current_user(user) && user.is_active) || (!user.is_active && !has_users_endpoint)"
                            :aria-label="user.is_active ? 'Deactivate user' : 'Activate user'"
                            :title="get_status_action_title(user)"
                        >
                            <span v-if="is_user_status_updating(user)" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <i v-else :class="user.is_active ? 'bi bi-person-dash' : 'bi bi-person-check'"></i>
                            <span>{{ user.is_active ? "Deactivate" : "Activate" }}</span>
                        </button>
                    </div>
                </td>
            </tr>
        </tbody>
    </table>
</div>
